import { useMemo, useState } from 'react';

import { RowSelectionState } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';

import { DocPickerTable } from './components/doc-picker-table';
import { useCreateDocSubset } from '@/api/mutations/useCreateDocSubset';
import { useGetAllDocs } from '@/api/queries/useGetAllDocs';
import { useGetCase } from '@/api/queries/useGetCase';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Textarea } from '@/new/components/shadcn/textarea';

export function SubsetCreator() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const navigate = useNavigate();
  const { data: matter } = useGetCase(matterId);
  const { data: docs } = useGetAllDocs(matterId);
  const createSubset = useCreateDocSubset();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);

  const [subsetName, setSubsetName] = useState('');
  const [subsetIssues, setSubsetIssues] = useState('');

  const selectedDocIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const handleCreateSubset = async () => {
    if (!subsetName || selectedDocIds.length === 0) return;

    try {
      const result = await createSubset.mutateAsync({
        caseId: matterId,
        body: {
          title: subsetName,
          legalIssues: subsetIssues,
          docIds: selectedDocIds,
        },
      });

      // Navigate to the subset detail page after creation
      navigate(`/matters/${matterId}/subsets/${result.id}`);
    } catch (error) {
      console.error('Failed to create subset:', error);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}`, label: matter.name, active: false },
                { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: false },
                { href: `/matters/${matterId}/subsets/new`, label: 'New', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer className="flex h-full flex-col overflow-hidden px-0">
        <div className="flex grow overflow-hidden">
          <section className="flex flex-col">
            <div className="flex max-w-sm items-center gap-4 p-7">
              <PageTitle text="Create a New Subset" iconClass="lucide--group" />

              <Popover>
                <PopoverTrigger>
                  <Icon className="lucide--info mt-1.25 text-base text-gray-400 hover:text-gray-600" />
                </PopoverTrigger>

                <PopoverContent side="right" sideOffset={14} className="mt-4 text-gray-800">
                  <div className="px-1">
                    Subsets allow you to easily analyse facts from a select group of documents.
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="m-2 mt-0 flex w-sm grow flex-col justify-between overflow-y-hidden rounded-md border bg-gray-50 shadow-lg/2">
              <div className="flex grow flex-col justify-between overflow-y-auto">
                <div className="flex flex-col gap-2 px-5 py-2">
                  <label>
                    <p className="my-2 text-gray-700">Subset Name</p>
                    <Input
                      type="text"
                      placeholder="Emails"
                      value={subsetName}
                      onChange={(e) => setSubsetName(e.target.value)}
                    />
                  </label>

                  <label>
                    <p className="my-2 text-gray-700">Subset Issues</p>
                    <Textarea
                      placeholder="All email communications from 2022"
                      value={subsetIssues}
                      onChange={(e) => setSubsetIssues(e.target.value)}
                      rows={8}
                    />
                  </label>

                  {/* <label>
                    <p className="my-2 text-gray-700">Subset Issues</p>
                    <Input type="text" placeholder="Were compliance complaints ignored?" />
                  </label>

                  <Button className="bg-gray-100 py-1.75" size="sm">
                    <Icon className="lucide--plus" />
                    <span>Add Issue</span>
                  </Button> */}
                </div>

                <p className="flex items-center gap-3 p-5 pr-6 text-balance text-gray-500">
                  <Icon className="lucide--info mb-0.75 text-gray-400" />
                  You can always add or remove documents from a Subset after creation.
                </p>
              </div>

              <div className="flex w-full justify-end rounded-b-md border-t bg-white p-4">
                <Button
                  variant="primary"
                  className="w-fit"
                  disabled={!selectedCount || !subsetName || createSubset.isLoading}
                  onClick={handleCreateSubset}
                >
                  <Icon className="lucide--group" />
                  <span>
                    {createSubset.isLoading
                      ? 'Creating...'
                      : `Create Subset ${selectedCount ? `with ${selectedCount} Document${selectedCount > 1 ? 's' : ''}` : ''}`}
                  </span>
                </Button>
              </div>
            </div>
          </section>

          <DocPickerTable docs={docs} rowSelection={rowSelection} onRowSelectionChange={setRowSelection} />
        </div>
      </PageContainer>
    </div>
  );
}
